/**
 * Markdown-it plugin for todo list items with eight marker states
 * @fileoverview Replaces markdown-it-task-lists. `- [X]` list items become
 * unicode glyph markers, matching the markdown-editor todo-checkbox
 * plugin output exactly (all icons are unicode).
 *
 *   ` ` open        → <span class="cm-todo-marker cm-todo-open" aria-label="open">○</span>
 *   `x` done        → <span class="cm-todo-marker cm-todo-done" aria-label="done">●</span>
 *   `/` in-progress → <span class="cm-todo-marker cm-todo-in-progress" aria-label="in-progress">◑</span>
 *   `!` important   → ◆
 *   `-` cancelled   → ◌
 *   `?` question    → ◇
 *   `>` deferred    → ▶
 *   `*` starred     → ★
 */

const TODO_MARKER_RE = /^\[([/ !x\-\?>\*X])\]\s*/;

const TODO_STATES = {
  ' ': { glyph: '○', state: 'open' },
  '/': { glyph: '◑', state: 'in-progress' },
  '!': { glyph: '◆', state: 'important' },
  'x': { glyph: '●', state: 'done' },
  '-': { glyph: '◌', state: 'cancelled' },
  '?': { glyph: '◇', state: 'question' },
  '>': { glyph: '▶', state: 'deferred' },
  '*': { glyph: '★', state: 'starred' },
};

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function todoCoreRule(state) {
  const tokens = state.tokens;
  const listStack = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (token.type === 'bullet_list_open' || token.type === 'ordered_list_open') {
      listStack.push(token);
      continue;
    }

    if (token.type === 'bullet_list_close' || token.type === 'ordered_list_close') {
      listStack.pop();
      continue;
    }

    if (token.type !== 'list_item_open') continue;

    // Find the inline token belonging to this list item.
    let inline = null;
    for (let j = i + 1; j < tokens.length; j++) {
      if (tokens[j].type === 'inline') { inline = tokens[j]; break; }
      if (tokens[j].type === 'list_item_close') break;
    }
    if (!inline || typeof inline.content !== 'string') continue;

    const m = TODO_MARKER_RE.exec(inline.content);
    if (!m) continue;

    const raw = m[1] === 'X' ? 'x' : m[1];
    const info = TODO_STATES[raw];
    if (!info) continue;

    const parent = listStack[listStack.length - 1];
    if (parent && !parent.attrGet('class')?.includes('contains-task-list')) {
      parent.attrJoin('class', 'contains-task-list');
    }

    token.attrJoin('class', 'cm-todo-item');

    const widget =
      `<span class="cm-todo-marker cm-todo-${info.state}" aria-label="${escapeHtml(info.state)}">` +
      `${info.glyph}</span> `;

    const widgetToken = new state.Token('html_inline', '', 0);
    widgetToken.content = widget;

    if (inline.children && inline.children.length && inline.children[0].type === 'text') {
      inline.children[0].content = inline.children[0].content.replace(TODO_MARKER_RE, '');
      inline.children.unshift(widgetToken);
    } else if (inline.children) {
      const textToken = new state.Token('text', '', 0);
      textToken.content = inline.content.replace(TODO_MARKER_RE, '');
      inline.children.unshift(widgetToken, textToken);
    }
    inline.content = inline.content.replace(TODO_MARKER_RE, '');
  }
}

export default function todoPlugin(md) {
  md.core.ruler.after('inline', 'todo_lists', todoCoreRule);
}
